import {OccupancyType} from "@features/leasing/leasing.model";

export interface Space {
  id: string
  districtUuid: string
  spaceType: string
  spaceName: string
  spaceLocation: string
  spaceSize: number // square meters
  locked: boolean
  startDate: string
  endDate: string
  districtSpacesIndustries: string[]
  hasHadLease: boolean
  earliestEndDate: string
  latestStartDate: string
  defaultPricing: SpaceDefaultRate[]
  available: boolean
}

export interface SpaceAvailability {
  occupiedSpaceIds: string[],
  availableSpaceIds: string[],
  occupiedLeaseSpaceIds: string[]
}

export interface SharedSpaceAvailability {
  totalSpace: number,
  occupiedSpace: number,
  availableSpace: number,
  occupiedLeaseSpaceIds: string[]
}

export interface SpaceDefaultRate {
  id: number
  spaceId: number
  rateType: RateType
  category: SpacePricingRateCategory
  timePeriod: GridSpacePricingTimePeriod
  value: number
  isDefault: boolean
  createdDate: string
  updatedDate: string
}

export enum RateType {
  FIXED = 'FIXED',
  VARIABLE = 'VARIABLE'
}

export enum SpacePricingRateCategory {
  PER_SQM = 'PER_SQM',
  PER_PERIOD = 'PER_PERIOD',
  PERCENTAGE = 'PERCENTAGE'
}

export enum GridSpacePricingTimePeriod {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  WEEKEND = 'WEEKEND'
}

export enum BookingType {
  SIMPLE = "SIMPLE",
  COMPLEX = "COMPLEX"
}

export enum PatternType {
  MONTHLY = "MONTHLY",
  WEEKLY = "WEEKLY",
  WEEKLY_CUSTOM = "WEEKLY_CUSTOM",
  WEEKENDS = "WEEKENDS",
  DAILY = "DAILY",
  DAILY_CUSTOM = "DAILY_CUSTOM"
}

export const perPricingPeriod: Record<GridSpacePricingTimePeriod, { period: string; patternType: PatternType[] }> = {
  [GridSpacePricingTimePeriod.DAILY]: {period: 'day', patternType: [PatternType.DAILY, PatternType.DAILY_CUSTOM]},
  [GridSpacePricingTimePeriod.WEEKLY]: {period: 'week', patternType: [PatternType.WEEKLY, PatternType.WEEKLY_CUSTOM]},
  [GridSpacePricingTimePeriod.MONTHLY]: {period: 'month', patternType: [PatternType.MONTHLY]},
  [GridSpacePricingTimePeriod.WEEKEND]: {period: 'weekend', patternType: [PatternType.WEEKENDS]},
}

export class BookingPricing {
  patternType: PatternType = PatternType.MONTHLY // dates & spaces
  includeDays: string[] = [] // dates & spaces
  excludeDays: string[] = [] // dates & spaces
  customRates: boolean = false;
  bookingRates: SpaceDefaultRate[] = [];
  bookingEstimation: BookingEstimation | null = null;
  spacePricingRateCategory: SpacePricingRateCategory | null = SpacePricingRateCategory.PER_PERIOD
  applyTransactionFees: boolean = false;
  initialBillablePeriods: any[] = [];
}

export class BookingConfig extends BookingPricing {
  retailerId: number | null = null // tenant info
  retailerCompanyName: string = '' // tenant info
  firstName: string = '' // tenant info
  lastName: string = '' // tenant info
  userEmail: string = '' // tenant info
  retailerIndustries: string[] = [] // tenant info
  userPhoneNumber: string = '' // tenant info
  spaceIds: string[] = [] // calendar view + dates & spaces
  startDate: string = ''
  endDate: string = ''
  bookingType: BookingType = BookingType.SIMPLE // dates & spaces
  selectedDates: string[] = [] // dates & spaces
  preSelectedDates: string[] = [] // calendar view
  spaceLimit: number = 2 // lease bookings (parent)
  subLeaseSize: number = 0
  occupancyType: OccupancyType = OccupancyType.SINGLE
}

export interface BookingSpaceConfig {
  pricingPeriodUuid: string,
  patternType: PatternType,
  includeDays: string[]
  excludeDays: string[]
}

export interface BookingEstimation {
  effectiveSpaces: string,
  totalSpace: number,
  periodType: GridSpacePricingTimePeriod,
  periodTypeUnit: string,
  leasePeriodDays: number,
  leasePeriods: number,
  fixedRate: number,
  variableRate: number,
  fixedCategoryType: SpacePricingRateCategory,
  variableCategoryType: SpacePricingRateCategory,
  fixedCost: number,
  variableCost: string,
  rentPerPeriod: number
}
